import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollManager from "./components/ScrollManager.js";
import Layout from "./components/Layout.js";
import ProtectedRoute from "./components/ProtectedRoute.js";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "./context/AuthContext.tsx";
import { DashboardLayout } from "@startup-spark-suite/components/layout/DashboardLayout.tsx";

// Lazy-loaded pages
const Index = lazy(() => import("./pages/Index.js"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage.js"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage.js"));
const SurveyPage = lazy(() => import("./pages/survey/SurveyPage.js"));
const SurveyResultsPage = lazy(() => import("./pages/survey/SurveyResultsPage.js"));
const NotFound = lazy(() => import("./pages/NotFound.js"));
const SettingsPage = lazy(() => import("./pages/Settings.js"));
const ContactUsWrapper = lazy(() => import("./components/ContactUsWrapper.tsx"));
const StartupSocialWrapper = lazy(() => import("./components/StartupSocialWrapper.tsx"));
const FAQ = lazy(() => import("./pages/FAQ.js"));
const PricingPageWrapper = lazy(() => import("./components/PricingPageWrapper.tsx"));
const SubmoduleApp = lazy(() => import('@startup-spark-suite/App.tsx'));
const Analytics = lazy(() => import("@startup-spark-suite/pages/Analytics.tsx"));
const FounderPods = lazy(() => import("@startup-spark-suite/pages/FounderPods.tsx"));
const PodMeeting = lazy(() => import("@startup-spark-suite/pages/PodMeeting.tsx"));
const SkillSwaps = lazy(() => import("@startup-spark-suite/pages/SkillSwap.tsx"));
const Investors = lazy(() => import("@startup-spark-suite/pages/Investors.tsx"));
const Events = lazy(() => import("@startup-spark-suite/pages/Events.tsx"));
const ArticlePage = lazy(() => import("./pages/ArticlePage.js"));
const Test = lazy(() => import("@startup-spark-suite/pages/Test.tsx"));
const Callback = lazy(() => import("./pages/Callback.tsx"));
const FeaturedArticlesWrapper = lazy(() => import("./components/FeaturedArticlesWrapper.tsx"));
const RedditSuccess = lazy(() => import("./pages/RedditSuccess.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const Demos = lazy(() => import("./pages/Demos.js"));


// Not lazy-loading DashboardIndex as it's not used in a Route
import DashboardIndex from '@startup-spark-suite/pages/Index.tsx'; // Renamed from SubmoduleIndex

const AuthenticatedRoutes = () => {
  const { user, signOut } = useAuth();
  return (
    <DashboardLayout user={user} signOut={signOut}>
      <Outlet />
    </DashboardLayout>
  );
};

const SurveyPageWrapper = () => {
  const { user } = useAuth();
  return <SurveyPage user={user} />;
};

const SubmoduleAppWrapper = () => {
  const { user, signOut } = useAuth();
  return <SubmoduleApp user={user} signOut={signOut} />;
};

function AppRoutes() {
  const { user, loading } = useAuth();

    if (loading) {
      return <div>Loading...</div>; // Or a more sophisticated loading
    }

  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <ScrollManager />
      <Suspense fallback={<div>Loading...</div>}>
        <Routes>
          <Route element={<Layout />}> {/* Layout is now the top-level wrapper */}
            <Route
              path="/"
              element={
                user ? (
                  <Navigate to="/startup-spark-suite" replace />
                ) : (
                  <Index /> // This is your landing page Index
                )
              }
            />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/payment-cancel" element={<PaymentCancelPage />} />
            <Route path="/FAQ" element={<FAQ />} />
            <Route path="/pricing" element={<PricingPageWrapper />} />
            <Route path="/survey/:slug/results" element={<SurveyResultsPage />} />
            <Route path="/article/:articleId" element={<ArticlePage />} />
            <Route path="/survey/:slug" element={<SurveyPageWrapper />} />

            <Route path="/ContactUs" element={<ContactUsWrapper />} />
            <Route path="/startup-social" element={<StartupSocialWrapper />} />
            <Route path="/startup-news" element={<FeaturedArticlesWrapper />} />
            <Route path="/demos" element={<Demos />} />

            {/* Authenticated routes, some with sidebar */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AuthenticatedRoutes />}> {/* Routes that need sidebar */}
                <Route path="/startup-growth" element={<FounderPods />} />
                <Route path="/pod/:id/meeting" element={<PodMeeting />} />
                <Route path="/skill-swaps" element={<SkillSwaps />} />
                <Route path="/startup-analytics" element={<Analytics />} />
                <Route path="/startup-spark-suite/investors" element={<Investors />} />
                <Route path="/startup-spark-suite/events" element={<Events />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/test" element={<Test />} />
                <Route path="/profile" element={<Navigate to={`/profile/${user?.id}`} replace />} />
                <Route path="/profile/:userId" element={<Profile />} />
              </Route>
              {/* Authenticated routes that do NOT need sidebar (or have their own layout) */}
              <Route path="/startup-spark-suite/*" element={<SubmoduleAppWrapper />} />
              <Route path="/dashboard" element={<Navigate to="/startup-spark-suite/" replace />} />
            </Route>

            <Route path="*" element={<NotFound />} /> {/* Catch-all for 404, still within Layout */}
          </Route>
          <Route path="/auth/github/callback" element={<Callback />} />
          <Route path="/auth/facebook/callback" element={<Callback />} />
          <Route path="/linkedin/callback" element={<Callback />} />
          <Route path="/reddit-success" element={<RedditSuccess />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default AppRoutes;